(() => {
  'use strict';

  const formatMoney = (cents, format = '{{ amount }}') => {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents || 0);
    const placeholder = (format.match(/{{\s*(\w+)\s*}}/) || [null, 'amount'])[1];

    const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
      if (!Number.isFinite(number)) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts[1] ? parts.join(decimal) : parts[0];
    };

    let money = '';
    switch (placeholder) {
      case 'amount_no_decimals': money = withDelimiters(value, 0); break;
      case 'amount_with_comma_separator': money = withDelimiters(value, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': money = withDelimiters(value, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': money = withDelimiters(value, 2, "'", '.'); break;
      default: money = withDelimiters(value, 2); break;
    }
    return format.replace(/{{\s*\w+\s*}}/, money);
  };

  const initHeader = (root = document) => {
    root.querySelectorAll('[data-locale-select]').forEach((select) => {
      if (select.dataset.bound) return;
      select.dataset.bound = 'true';
      select.addEventListener('change', () => select.form?.submit());
    });

    root.querySelectorAll('[data-menu-drawer]').forEach((drawer) => {
      if (drawer.dataset.bound) return;
      drawer.dataset.bound = 'true';

      const trigger = drawer.querySelector('summary');
      const panel = drawer.querySelector('.mc-menu-drawer__panel');
      const closeButton = drawer.querySelector('[data-drawer-close]');
      const overlay = drawer.querySelector('[data-drawer-overlay]');
      const links = drawer.querySelectorAll('[data-drawer-link]');
      const desktopMedia = window.matchMedia('(min-width: 990px)');

      const focusable = () => Array.from(panel?.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])') || []);
      const setPageLock = (locked) => {
        document.documentElement.classList.toggle('mc-drawer-open', locked);
        document.body.classList.toggle('mc-drawer-open', locked);
      };
      const closeDrawer = (restoreFocus = true) => {
        if (!drawer.open) return;
        drawer.removeAttribute('open');
        trigger?.setAttribute('aria-expanded', 'false');
        setPageLock(false);
        if (restoreFocus) trigger?.focus();
      };
      const syncDrawer = () => {
        const open = drawer.open;
        trigger?.setAttribute('aria-expanded', String(open));
        setPageLock(open);
        if (open) window.requestAnimationFrame(() => closeButton?.focus());
      };

      trigger?.setAttribute('aria-expanded', 'false');
      drawer.addEventListener('toggle', syncDrawer);
      closeButton?.addEventListener('click', () => closeDrawer());
      overlay?.addEventListener('click', () => closeDrawer());
      links.forEach((link) => link.addEventListener('click', () => closeDrawer(false)));
      desktopMedia.addEventListener?.('change', (event) => { if (event.matches) closeDrawer(false); });

      drawer.addEventListener('keydown', (event) => {
        if (!drawer.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          closeDrawer();
          return;
        }
        if (event.key !== 'Tab') return;
        const items = focusable();
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      });
    });
  };

  const initGallery = (section) => {
    const gallery = section.querySelector('[data-product-gallery]');
    if (!gallery || gallery.dataset.bound) return;
    gallery.dataset.bound = 'true';

    const showMedia = (id) => {
      gallery.querySelectorAll('[data-media-slide]').forEach((slide) => {
        slide.hidden = String(slide.dataset.mediaSlide) !== String(id);
      });
      gallery.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
        const active = String(thumb.dataset.mediaThumb) === String(id);
        thumb.classList.toggle('is-active', active);
        if (active) {
          thumb.setAttribute('aria-current', 'true');
          thumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
    };

    gallery.querySelectorAll('[data-media-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => showMedia(thumb.dataset.mediaThumb));
    });

    const dialog = section.querySelector('[data-gallery-dialog]');
    section.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => {
      if (!dialog) return;
      const currentImage = gallery.querySelector('[data-media-slide]:not([hidden]) img');
      const target = dialog.querySelector('[data-gallery-dialog-content]');
      if (!currentImage || !target) return;
      target.replaceChildren(currentImage.cloneNode(true));
      if (typeof dialog.showModal === 'function') dialog.showModal();
    });
    dialog?.querySelector('[data-gallery-close]')?.addEventListener('click', () => dialog.close());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    return showMedia;
  };

  const initProduct = (section) => {
    if (section.dataset.bound) return;
    section.dataset.bound = 'true';

    const thankYou = section.querySelector('[data-cod-thank-you]');
    const productContent = section.querySelector('[data-product-content]');
    const query = new URLSearchParams(window.location.search);
    if (query.get('contact_posted') === 'true' && thankYou) {
      thankYou.hidden = false;
      if (productContent) productContent.hidden = true;
      section.classList.add('is-thank-you');
      document.title = `شكراً لك — ${document.title}`;
      window.requestAnimationFrame(() => thankYou.focus());
      return;
    }

    const showMedia = initGallery(section);
    const jsonNode = section.querySelector('[data-variant-json]');
    const form = section.querySelector('.mc-product-form');
    if (!jsonNode || !form) return;

    let data;
    try { data = JSON.parse(jsonNode.textContent); } catch (error) { console.error('Variant data could not be read.', error); return; }
    const variantInput = form.querySelector('[data-variant-id]');
    const price = section.querySelector('[data-product-price]');
    const compare = section.querySelector('[data-product-compare]');
    const saving = section.querySelector('[data-product-saving]');
    const stock = section.querySelector('[data-product-stock]');
    const leadButton = form.querySelector('[data-lead-submit]');
    const leadLabel = form.querySelector('[data-lead-label]');
    const leadVariantTitle = form.querySelector('[data-lead-variant-title]');
    const leadSelection = form.querySelector('[data-lead-selection]');
    const leadPrice = form.querySelector('[data-lead-price]');
    const leadSummary = form.querySelector('[data-lead-summary]');
    const leadProduct = form.querySelector('input[name="contact[Produit]"]');
    const threshold = Number(section.dataset.lowStockThreshold || 8);
    const showInventoryCount = section.dataset.showInventoryCount === 'true';

    const currentOptions = () => Array.from(form.querySelectorAll('[data-option-position]')).map((fieldset) => {
      return fieldset.querySelector('input[type="radio"]:checked')?.value;
    });

    const findVariant = () => {
      const options = currentOptions();
      return data.variants.find((variant) => variant.options.every((option, index) => option === options[index]));
    };

    const renderStock = (variant) => {
      if (!stock) return;
      if (!variant?.available) {
        stock.innerHTML = '<span class="mc-stock__dot mc-stock__dot--out"></span><span></span>';
        stock.querySelector('span:last-child').textContent = data.labels.sold_out;
        return;
      }
      const isLow = variant.inventory_management && variant.inventory_policy === 'deny' && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold;
      stock.innerHTML = `<span class="mc-stock__dot${isLow ? ' mc-stock__dot--low' : ''}"></span><span></span>`;
      const lowLabel = showInventoryCount ? `${data.labels.low_stock}: ${variant.inventory_quantity}` : data.labels.low_stock;
      stock.querySelector('span:last-child').textContent = isLow ? lowLabel : data.labels.in_stock;
    };

    const renderVariant = (variant) => {
      const available = Boolean(variant?.available);
      const formattedPrice = variant ? formatMoney(variant.price, data.money_format) : '';
      if (variantInput) variantInput.value = variant?.id || '';
      if (leadVariantTitle) leadVariantTitle.value = variant?.title || '';
      if (leadSelection) leadSelection.textContent = variant?.title && variant.title !== 'Default Title' ? variant.title : 'Produit standard / المنتج';
      if (leadPrice) leadPrice.value = formattedPrice;
      if (leadSummary) leadSummary.value = `Demande COD Velora Store — ${leadProduct?.value || ''} — ${variant?.title || ''} — ${formattedPrice}`;
      if (price) price.textContent = variant ? formattedPrice : data.labels.sold_out;

      const hasSale = variant && variant.compare_at_price > variant.price;
      if (compare) {
        compare.hidden = !hasSale;
        compare.textContent = hasSale ? formatMoney(variant.compare_at_price, data.money_format) : '';
      }
      if (saving) {
        saving.hidden = !hasSale;
        const percent = hasSale ? Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100) : 0;
        saving.textContent = hasSale ? `${data.labels.savings} ${percent}%` : '';
      }

      renderStock(variant);
      if (leadButton) leadButton.disabled = !available;
      if (leadLabel) leadLabel.textContent = available ? data.labels.cta : data.labels.sold_out;
      document.querySelectorAll('[data-sticky-price]').forEach((node) => {
        if (variant) node.textContent = formatMoney(variant.price, data.money_format);
      });

      if (variant?.featured_media_id && showMedia) showMedia(variant.featured_media_id);
      if (variant?.id) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    };

    form.querySelectorAll('[data-variant-options] input[type="radio"]').forEach((input) => {
      input.addEventListener('change', () => renderVariant(findVariant()));
    });

    const quantity = form.querySelector('[data-quantity] input');
    form.querySelector('[data-quantity-minus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
    });
    form.querySelector('[data-quantity-plus]')?.addEventListener('click', () => {
      if (!quantity) return;
      quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
    });

    renderVariant(data.variants.find((variant) => String(variant.id) === String(variantInput?.value)) || findVariant());
  };

  const initStickyOrder = (root = document) => {
    root.querySelectorAll('[data-sticky-order]').forEach((sticky) => {
      if (sticky.dataset.bound) return;
      sticky.dataset.bound = 'true';
      if (document.querySelector('[data-cod-thank-you]:not([hidden])')) return;
      const purchase = document.querySelector('[data-purchase-panel]');
      const target = purchase?.querySelector('[data-easysell-zone]') || purchase?.querySelector('[data-cod-fields]') || purchase?.querySelector('.mc-product-form') || purchase;
      sticky.querySelector('[data-sticky-scroll]')?.addEventListener('click', () => target?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      if (!purchase || window.matchMedia('(min-width: 990px)').matches) return;

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(([entry]) => {
          sticky.hidden = entry.isIntersecting;
        }, { threshold: 0.18 });
        observer.observe(purchase);
      } else {
        sticky.hidden = false;
      }
    });
  };

  const init = (root = document) => {
    initHeader(root);
    root.querySelectorAll('[data-product-section]').forEach(initProduct);
    initStickyOrder(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
